// src/components/chat/ImageConsentBanner.tsx
// Image consent status banner for chat modals

import React, { memo } from 'react';
import { IonIcon, IonButton } from '@ionic/react';
import { checkmarkCircle, closeCircle, imageOutline } from 'ionicons/icons';

interface ImageConsentStatus {
  enabled: boolean;
  requestedBy?: string;
  requestedAt?: string;
  canRequest: boolean;
  canApprove: boolean;
}

interface ImageConsentBannerProps {
  imageConsentStatus: ImageConsentStatus | null;
  otherUserName: string;
  loadingImageConsent: boolean;
  onApprove: () => void;
  onReject: () => void;
  onRequest: () => void;
}

export const ImageConsentBanner: React.FC<ImageConsentBannerProps> = memo(({
  imageConsentStatus,
  otherUserName,
  loadingImageConsent,
  onApprove,
  onReject,
  onRequest
}) => {
  if (!imageConsentStatus) {
    return null;
  }

  // Images are enabled
  if (imageConsentStatus.enabled) {
    return (
      <div style={{
        padding: '8px 16px',
        backgroundColor: 'var(--ion-color-success)',
        borderBottom: '1px solid var(--ion-color-success-shade)',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
      }}>
        <IonIcon icon={checkmarkCircle} style={{ color: '#ffffff' }} />
        <span style={{ fontSize: '13px', color: '#ffffff', fontWeight: '500' }}>
          Image sharing enabled
        </span>
      </div>
    );
  }

  // Pending request - user can approve
  if (imageConsentStatus.canApprove) {
    return (
      <div style={{
        padding: '12px 16px',
        backgroundColor: 'var(--ion-card-background)',
        borderBottom: '1px solid var(--ion-border-color)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
          <IonIcon icon={imageOutline} style={{ color: 'var(--ion-color-primary)' }} />
          <span style={{ fontSize: '14px', fontWeight: '500' }}>
            {otherUserName} wants to enable image sharing
          </span>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <IonButton
            size="small"
            onClick={onApprove}
            disabled={loadingImageConsent}
            style={{ flex: 1 }}
          >
            <IonIcon slot="start" icon={checkmarkCircle} />
            Approve
          </IonButton>
          <IonButton
            size="small"
            fill="outline"
            color="medium"
            onClick={onReject}
            disabled={loadingImageConsent}
            style={{ flex: 1 }}
          >
            <IonIcon slot="start" icon={closeCircle} />
            Decline
          </IonButton>
        </div>
      </div>
    );
  }

  // Request pending (current user requested)
  if (imageConsentStatus.requestedBy) {
    return (
      <div style={{
        padding: '12px 16px',
        backgroundColor: 'var(--ion-card-background)',
        borderBottom: '1px solid var(--ion-border-color)',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
        <IonIcon icon={imageOutline} style={{ color: 'var(--ion-color-medium)' }} />
        <span style={{ fontSize: '14px', color: 'var(--ion-color-medium)' }}>
          Image sharing request pending...
        </span>
      </div>
    );
  }

  // Can request
  if (imageConsentStatus.canRequest) {
    return (
      <div style={{
        padding: '12px 16px',
        backgroundColor: 'var(--ion-card-background)',
        borderBottom: '1px solid var(--ion-border-color)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
          <IonIcon icon={imageOutline} style={{ color: 'var(--ion-text-color)' }} />
          <span style={{ fontSize: '14px', color: 'var(--ion-text-color)' }}>
            Image sharing disabled
          </span>
        </div>
        <IonButton
          size="small"
          fill="clear"
          onClick={onRequest}
          disabled={loadingImageConsent}
          style={{ margin: 0, height: '32px' }}
        >
          Request image sharing
        </IonButton>
      </div>
    );
  }

  return null;
});

ImageConsentBanner.displayName = 'ImageConsentBanner';
